import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * Accessibility (WCAG 2.2.2): automatic rotation can be stopped with the pause button, waits while the
 * pointer or keyboard focus is in the bar, and doesn't start for visitors who prefer reduced motion.
 * Screen readers only hear the slide changes the visitor makes, not every automatic one.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 * @property {HTMLButtonElement} [playback]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('focusin', this.suspend);
    this.addEventListener('focusout', this.#handleFocusOut);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.pause();
    } else {
      this.play();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.suspend();
  }

  next() {
    this.#announceChanges(true);
    this.current += 1;
  }

  previous() {
    this.#announceChanges(true);
    this.current -= 1;
  }

  /**
   * Pauses or restarts automatic playback (the pause/play button).
   */
  togglePlayback() {
    if (this.paused) {
      this.play();
    } else {
      this.pause();
    }
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;
    this.suspend();

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || this.matches(':focus-within') || document.hidden) return;

      this.#announceChanges(false);
      this.current += 1;
    }, interval);
  }

  /**
   * Pauses automatic slide playback until the visitor presses play.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);

    const { playback } = this.refs;
    if (playback) {
      playback.setAttribute('aria-label', `${paused ? playback.dataset.playLabel : playback.dataset.pauseLabel}`);
    }
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  /**
   * Only slide changes the visitor asks for are read out.
   * @param {boolean} announce
   */
  #announceChanges(announce) {
    this.setAttribute('aria-live', announce ? 'polite' : 'off');
  }

  /**
   * Resume once keyboard focus has left the bar.
   * @param {FocusEvent} event
   */
  #handleFocusOut = (event) => {
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;

    this.resume();
  };

  /**
   * Hold the slideshow while the page is hidden, and carry on when it's back.
   */
  #handleVisibilityChange = () => (document.hidden ? this.suspend() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
